import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { GA4EventName, trackAnalyticsEvent } from '@jsmdg/tracking';
import { ChevronIcon, Link, LinkVariant } from '@jsmdg/yoshi';
import { type ProductDetailContext } from '../../types/types';
import { PageRecommendations } from './PageRecommendations/PageRecommendations';
import { ProductRecommendations } from './ProductRecommendations/ProductRecommendations';
import styles from './Recommendations.module.scss';

const messages = defineMessages({
    pagesTitle: {
        defaultMessage: 'Entdecke beliebte Kategorien',
    },
    productsTitle: {
        defaultMessage: 'Entdecke ähnliche Erlebnisse',
    },
    categoryLink: {
        defaultMessage: 'Mehr sehen',
    },
});

const onCategoryLinkClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    trackAnalyticsEvent({
        eventData: {
            eventName: GA4EventName.ClickTextLink,
            feature_category: 'Productdetail information',
            click_element: 'Go to category page',
            click_text: 'Mehr sehen',
        },
    });
    location.href =
        window?.Evelin.data?.['header-fragment']?.rootComponentProps?.breadcrumbs?.[0]?.url;
};

const Recommendations = (): JSX.Element | null => {
    const intl = useIntl();
    const { featureFlagsVariation } = useFragmentContext<ProductDetailContext>();

    const isPageRecommendationsEnabled =
        featureFlagsVariation?.google_trafic_recommendations === 'pages';

    const isProductRecommendationsEnabled =
        featureFlagsVariation?.google_trafic_recommendations === 'products';

    if (!isPageRecommendationsEnabled && !isProductRecommendationsEnabled) {
        return null;
    }

    return (
        <div
            className={classNames(
                styles.recommendationsContainer,
                'mb-3x mb-sm-5x pb-0 pb-sm-0-5x',
            )}
        >
            <div
                className={classNames(
                    styles.overlay,
                    'position-absolute start-0 end-0 d-none d-sm-block',
                )}
            />
            <div className="d-flex align-items-center justify-content-between px-3x py-1x p-sm-0 pb-sm-1-5x">
                <h2 className={classNames(styles.recommendationsTitle, 'm-0 p-0')}>
                    {isPageRecommendationsEnabled
                        ? intl.formatMessage(messages.pagesTitle)
                        : intl.formatMessage(messages.productsTitle)}
                </h2>
                {!isPageRecommendationsEnabled && (
                    <Link
                        href="#"
                        onClick={onCategoryLinkClick}
                        variant={LinkVariant.Brand}
                        className={classNames('d-flex align-items-center', styles.categoryLink)}
                    >
                        {intl.formatMessage(messages.categoryLink)}
                        <ChevronIcon className={styles.chevronIcon} size={18} />
                    </Link>
                )}
            </div>
            <div className="pt-1x pt-sm-1-5x pl-3x pr-xs-3x px-sm-0">
                {isPageRecommendationsEnabled && <PageRecommendations />}
                {isProductRecommendationsEnabled && <ProductRecommendations />}
            </div>
        </div>
    );
};

export { Recommendations };
